import React from 'react';
import { useQuery } from '@apollo/client';
import { ShopCart, User } from '../models';
import { GET_USER } from '../graphql/queries';
import { UserContext } from './UserContext';

type TShopCartContext = {
  shopCart: ShopCart;
  setShopCart: React.Dispatch<React.SetStateAction<ShopCart>>;
  refreshShopCart: () => Promise<void>;
};

export const ShopCartContext = React.createContext<TShopCartContext>({
  shopCart: null,
  setShopCart: null,
  refreshShopCart: null,
});

interface ShopCartContextProviderProps {
  children: React.ReactNode;
}

export function ShopCartContextProvider({
  children,
}: ShopCartContextProviderProps) {
  const [shopCart, setShopCart] = React.useState<ShopCart>(null);
  const { user } = React.useContext(UserContext);

  const { fetchMore, loading } = useQuery<{ user: User }>(GET_USER, {
    variables: {
      filter: {
        _id: user?._id,
      },
    },
    fetchPolicy: 'network-only',
  });
  const refreshShopCart = async () => {
    await fetchMore({
      variables: {
        filter: {
          _id: user?._id,
        },
      },
    });
  };

  React.useEffect(() => {
    if (user && user?.client && user?.client?.shopCart) {
      setShopCart(user?.client?.shopCart);
    }
  }, [user]);

  const value = React.useMemo(
    () => ({ shopCart, setShopCart, refreshShopCart }),
    [shopCart]
  );
  return (
    <ShopCartContext.Provider value={value}>
      {loading ? null : children}
    </ShopCartContext.Provider>
  );
}
